import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function PortfolioPreview() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  const projects = [
    {
      titleKey: 'portfolio.project1',
      categoryKey: 'portfolio.project1Cat',
      descKey: 'portfolio.project1Desc',
      image: 'https://readdy.ai/api/search-image?query=Premium%20modern%20ecommerce%20website%20design%20on%20laptop%20screen%20with%20luxury%20product%20display%20and%20clean%20minimalist%20layout%20dark%20theme%20with%20red%20accents%20professional%20marketing%20agency%20portfolio%20showcase&width=1200&height=750&seq=portfolio-01&orientation=landscape',
      stat: '+70%',
      statLabelKey: 'portfolio.project1Stat',
    },
    {
      titleKey: 'portfolio.project2',
      categoryKey: 'portfolio.project2Cat',
      descKey: 'portfolio.project2Desc',
      image: 'https://readdy.ai/api/search-image?query=Modern%20digital%20marketing%20analytics%20dashboard%20on%20tablet%20and%20laptop%20screens%20showing%20lead%20generation%20metrics%20graphs%20and%20conversion%20data%20dark%20sleek%20background%20with%20red%20highlights&width=1200&height=750&seq=portfolio-02&orientation=landscape',
      stat: '+200',
      statLabelKey: 'portfolio.project2Stat',
    },
    {
      titleKey: 'portfolio.project3',
      categoryKey: 'portfolio.project3Cat',
      descKey: 'portfolio.project3Desc',
      image: 'https://readdy.ai/api/search-image?query=Premium%20branding%20design%20mockup%20on%20smartphone%20and%20laptop%20with%20modern%20typography%20and%20red%20color%20scheme%20dark%20sophisticated%20background%20creative%20agency%20portfolio%20luxury%20aesthetic&width=1200&height=750&seq=portfolio-03&orientation=landscape',
      stat: '+520%',
      statLabelKey: 'portfolio.project3Stat',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % projects.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const project = projects[activeIndex];

  return (
    <section id="portfolio" className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <ScrollReveal direction="up" duration={0.7}>
          <div className="text-center mb-14 md:mb-18">
            <span className="inline-block px-4 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-600 text-xs font-semibold tracking-wider uppercase mb-5">
              {t('portfolio.tag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {t('portfolio.title')}{' '}
              <span className="text-primary-500">{t('portfolio.titleHighlight')}</span>
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-xl mx-auto">
              {t('portfolio.subtitle')}
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={0.2} duration={0.8}>
          <div className="relative">
            <div className="bg-gray-900 rounded-3xl overflow-hidden p-4 md:p-6">
              <div className="relative rounded-2xl overflow-hidden bg-gray-800 h-[300px] md:h-[450px]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={activeIndex}
                    src={project.image}
                    alt={t(project.titleKey)}
                    className="absolute inset-0 w-full h-full object-cover"
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent"></div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    className="absolute bottom-0 left-0 right-0 p-6 md:p-10"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-red-400 text-sm font-medium">{t(project.categoryKey)}</span>
                    </div>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                      {t(project.titleKey)}
                    </h3>
                    <p className="text-gray-300 text-sm md:text-base max-w-lg mb-4">
                      {t(project.descKey)}
                    </p>
                    <div className="flex items-center gap-6">
                      <div>
                        <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500">
                          {project.stat}
                        </div>
                        <div className="text-gray-400 text-xs mt-1">{t(project.statLabelKey)}</div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            <div className="flex justify-center gap-2 mt-6">
              {projects.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-500 cursor-pointer ${
                    i === activeIndex
                      ? 'w-8 bg-primary-500'
                      : 'bg-gray-300 hover:bg-gray-500'
                  }`}
                  aria-label={`Project ${i + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}